import { useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FormField } from "@/components/auth/FormField";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useChapters, useSubjects } from "@/hooks/useCurriculum";
import { useCreateTest } from "@/hooks/useTests";
import { cn } from "@/lib/cn";
import type { TestDifficulty } from "@/lib/api-types";

const DIFFICULTIES: { value: TestDifficulty; label: string }[] = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
  { value: "mixed", label: "Mixed" },
];
const LENGTHS = [5, 10, 15, 20];

/**
 * Self-serve practice test creation (Phase 7) -- a trimmed version of the
 * parent wizard: subject -> chapters -> difficulty -> length, no separate
 * "assign" step (auto-assigns to self on the backend).
 */
export function CreatePracticeTestForm({ onCreated }: { onCreated: () => void }) {
  const me = useCurrentUser();
  const subjects = useSubjects(me.data?.class_id ?? undefined);
  const createTest = useCreateTest();

  const [subjectId, setSubjectId] = useState("");
  const [chapterIds, setChapterIds] = useState<string[]>([]);
  const [difficulty, setDifficulty] = useState<TestDifficulty>("mixed");
  const [numQuestions, setNumQuestions] = useState(10);
  const chapters = useChapters(subjectId || undefined);

  const onSubmit = async () => {
    if (!subjectId || chapterIds.length === 0) return;
    try {
      const data = await createTest.mutateAsync({
        subject_id: subjectId,
        chapter_ids: chapterIds,
        difficulty,
        num_questions: numQuestions,
      });
      if (data?.assignment_id) {
        toast.success(`Creating your test... (${data.quota.used}/${data.quota.limit} today)`);
        onCreated();
      }
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      const message =
        status === 429
          ? "Daily practice-test limit reached for today."
          : (err as { response?: { data?: { status_message?: string } } }).response?.data?.status_message ||
            "Couldn't create that test.";
      toast.error(message);
    }
  };

  return (
    <Card className="p-4 sm:p-5">
      <p className="font-medium text-sm mb-3">Create a practice test</p>

      <FormField id="practice_subject" label="Subject">
        <div className="flex flex-wrap gap-2">
          {subjects.isLoading ? (
            <Skeleton className="h-9 w-40" />
          ) : (
            (subjects.data ?? []).map((s) => (
              <button
                key={s.subject_id}
                type="button"
                onClick={() => {
                  setSubjectId(s.subject_id);
                  setChapterIds([]);
                }}
                className={cn(
                  "rounded-lg border px-3 py-1.5 text-sm transition-colors",
                  subjectId === s.subject_id ? "border-brand bg-brand-soft" : "border-border hover:bg-surface-2",
                )}
              >
                {s.subject_name}
              </button>
            ))
          )}
        </div>
      </FormField>

      {subjectId ? (
        <FormField id="practice_chapters" label="Chapters" className="mt-3" hint={`Up to 5. ${chapterIds.length}/5 selected.`}>
          <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto">
            {chapters.isLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              (chapters.data ?? []).map((c) => {
                const checked = chapterIds.includes(c.chapter_id);
                return (
                  <label
                    key={c.chapter_id}
                    className={cn(
                      "flex items-center gap-2.5 rounded-lg border px-3 py-2 cursor-pointer transition-colors text-sm",
                      checked ? "border-brand bg-brand-soft" : "border-border hover:bg-surface-2",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setChapterIds((prev) =>
                          checked ? prev.filter((id) => id !== c.chapter_id) : [...prev, c.chapter_id].slice(0, 5),
                        )
                      }
                      className="h-4 w-4 accent-brand"
                    />
                    {c.chapter_name}
                  </label>
                );
              })
            )}
          </div>
        </FormField>
      ) : null}

      <div className="mt-3 grid grid-cols-2 gap-3">
        <FormField id="practice_difficulty" label="Difficulty">
          <select
            id="practice_difficulty"
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value as TestDifficulty)}
            className="h-10 rounded-lg border border-border bg-surface px-3 text-sm text-text focus-visible:outline-none focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-ring/40"
          >
            {DIFFICULTIES.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </FormField>
        <FormField id="practice_length" label="Questions">
          <select
            id="practice_length"
            value={numQuestions}
            onChange={(e) => setNumQuestions(Number(e.target.value))}
            className="h-10 rounded-lg border border-border bg-surface px-3 text-sm text-text focus-visible:outline-none focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-ring/40"
          >
            {LENGTHS.map((n) => (
              <option key={n} value={n}>{n} questions</option>
            ))}
          </select>
        </FormField>
      </div>

      <Button
        className="mt-4"
        fullWidth
        loading={createTest.isPending}
        disabled={!subjectId || chapterIds.length === 0}
        onClick={onSubmit}
      >
        Create test
      </Button>
    </Card>
  );
}
